<template>
  <b-col id="telemetryContainer" class="flex-column d-flex p-2 p-md-4">
    <b-card id="telemInputs" header-tag="header" class="mb-2 mb-md-3">
      <div slot="header">
        Inputs
        <div class="card-header-actions">
          <font-awesome-icon
            size="lg"
            class="ml-2 h-100"
            :style="{'cursor': 'pointer', 'user-select': 'none', 'color': 'gray'}"
            :icon="inputsCollapsed ? 'chevron-down' : 'chevron-up'"
            v-b-toggle.inputsCollapse />
        </div>
      </div>
      <b-collapse visible id="inputsCollapse" @show="inputsCollapsed = false" @hide="inputsCollapsed = true">
        <b-row>
          <b-col class="col-md-6">
            <gcu-dropdown v-model="selectedGCU" :multiple="false" :spacing="colSpacing" cacheKey="telemetry"></gcu-dropdown>
            <tracker-dropdown v-model="selectedTracker" :gcu="selectedGCU ? selectedGCU.id : null" :multiple="false"
              :spacing="colSpacing" :disabled="disabled.tracker" cacheKey="telemetry"></tracker-dropdown>
            <b-form-group
              label="Armature"
              :label-cols="colSpacing"
              :horizontal="true">
              <multiselect v-model="selectedArm" :options="armatures" :multiple="true" selectLabel="" :close-on-select="false"
                deselectLabel="Remove" placeholder="Select Armature" :disabled="disabled.armature"></multiselect>
            </b-form-group>
          </b-col>
          <b-col class="col-md-6">
            <b-form-group
              label="Telemetry"
              label-for="selectedTelemetry"
              :label-cols="colSpacing"
              :horizontal="true">
              <multiselect v-model="selectedTelemetry" :options="telemOpts" group-values="libs" group-label="component" :multiple="true"
                 :close-on-select="false" label="label" track-by="label" selectLabel="" deselectLabel="Remove"></multiselect>
            </b-form-group>
            <b-form-group
              label="Start Datetime"
              label-for="startDateTime"
              :label-cols="colSpacing"
              :horizontal="true">
              <datetime id="startDateTime" input-class="form-control" v-model="date.start" input-id="telemStartDate" type="datetime"
                format="yyyy-LL-dd HH:mm:ss" auto value-zone="local" placeholder="Select Date" :max-datetime="date.max"
                class="theme-msi-orange">
              </datetime>
            </b-form-group>
            <b-form-group
              label="End Datetime"
              label-for="endDateTime"
              :label-cols="colSpacing"
              :horizontal="true">
              <datetime id="endDateTime" input-class="form-control" v-model="date.end" input-id="telemEndDate" type="datetime"
                format="yyyy-LL-dd HH:mm:ss" auto value-zone="local" placeholder="Select Date" :min-datetime="date.start"
									:max-datetime="date.max" class="theme-msi-orange">
              </datetime>
            </b-form-group>
            <b-row class="d-flex" style="justify-content: flex-end;">
              <div>
                <img style="margin-right: 15px;" v-show="show.loading" src="img/msi_spinner.gif" height="25" width="25">
                <button style="margin-right: 15px;" class="btn btn-primary" @click="getPlotData()" :disabled="disabled.search">Plot Selection</button>
                <button style="margin-right: 15px;" class="btn btn-danger" @click="clearPlotData()">Clear Plot</button>
              </div>
            </b-row>
          </b-col>
        </b-row>
      </b-collapse>
    </b-card>
    <b-card id="plot" class="flex-grow-1 flex-shrink-1 m-0" header="Plot">
      <highcharts :options="lineOptions"></highcharts>
    </b-card>
  </b-col>
</template>

<script>
import Vue from 'vue';
import Multiselect from 'vue-multiselect';
import moment from 'moment';
import GcuDropdown from '../components/GcuDropdown';
import TrackerDropdown from '../components/TrackerDropdown';
import SiteDropdown from '../components/SiteDropdown';

const colSpacing = 4;

// options to populate the telemetry input
// the multitude of properties for each option allows for programmatic selections later
const telemOpts = [];
const trackerTelemOpts = {
  component: 'Tracker Options',
  libs: [
    {
      value: 'trackerStatus', label: 'Tracker Status', type: 'Status', lookup: 'trackerStatus', option: 'tracker'
    },
    {
      value: 'halt', label: 'Halt Cycle', type: 'Reed', subType: 'reedon', lookup: 'halt_cycle', option: 'tracker'
    }
  ]
};
const armatureTelemOpts = {
  component: 'Armature Options',
  libs: [
    {
      value: 'curArmAzi', label: 'Azimuth', type: 'Status', lookup: 'azimuth', option: 'armature'
    },
    {
      value: 'curArmElev', label: 'Elevation', type: 'Status', lookup: 'elevation', option: 'armature'
    },
    {
      value: 'armStatus', label: 'Armature Status', type: 'Status', lookup: 'error_code', option: 'armature'
    },
    {
      value: 'reedOnArmAzi', label: 'Reed On Azimuth', type: 'Reed', subType: 'reedon', lookup: 'azimuth', option: 'armature'
    },
    {
      value: 'reedOnArmElev', label: 'Reed On Elevation', type: 'Reed', subType: 'reedon', lookup: 'elevation', option: 'armature'
    },
    {
      value: 'reedOffArmAzi', label: 'Reed Off Azimuth', type: 'Reed', subType: 'reedoff', lookup: 'azimuth', option: 'armature'
    },
    {
      value: 'reedOffArmElev', label: 'Reed Off Elevation', type: 'Reed', subType: 'reedoff', lookup: 'elevation', option: 'armature'
    },
    {
      value: 'stepsArmAzi', label: 'Steps Azimuth', type: 'Reed', subType: 'steps', lookup: 'azimuth', option: 'armature'
    },
    {
      value: 'stepsArmElev', label: 'Steps Elevation', type: 'Reed', subType: 'steps', lookup: 'elevation', option: 'armature'
    },
    {
      value: 'solarSensorTotal', label: 'Solar Sensor Total', type: 'Sensor', lookup: 'total', option: 'armature'
    },
    {
      value: 'solarSensorAzi', label: 'Solar Sensor Azimuth', type: 'Sensor', lookup: 'azimuth', option: 'armature'
    },
    {
      value: 'solarSensorElev', label: 'Solar Sensor Elevation', type: 'Sensor', lookup: 'elevation', option: 'armature'
    }
  ]
};
const gcuTelemOpts = {
  component: 'GCU Options',
  libs: [
    {
      value: 'global_irradiance', label: 'GHI', type: 'Weather', lookup: 'irrad', option: 'gcu'
    },
    {
      value: 'temperature', label: 'Temperature', type: 'Weather', lookup: 'temp', option: 'gcu'
    },
    {
      value: 'humidity', label: 'Humidity', type: 'Weather', lookup: 'temp', option: 'gcu'
    },
    {
      value: 'pressure', label: 'Pressure', type: 'Weather', lookup: 'temp', option: 'gcu'
    },
    {
      value: 'wind_direction', label: 'Wind Direction', type: 'Weather', lookup: 'wind', option: 'gcu'
    },
    {
      value: 'wind_speed', label: 'Wind Speed', type: 'Weather', lookup: 'wind', option: 'gcu'
    },
    {
      value: 'wind_gust', label: 'Wind Gust', type: 'Weather', lookup: 'wind', option: 'gcu'
    },
    {
      value: 'GNI', label: 'GNI', type: 'Irrad', lookup: 'irrad', option: 'gcu'
    },
    {
      value: 'DNI', label: 'DNI', type: 'Irrad', lookup: 'irrad', option: 'gcu'
    },
    {
      value: 'GFTI', label: 'GFTI', type: 'Irrad', lookup: 'irrad', option: 'gcu'
    },
    {
      value: 'GBTI', label: 'GBTI', type: 'Irrad', lookup: 'irrad', option: 'gcu'
    },
    {
      value: 'GSTI', label: 'GSTI', type: 'Irrad', lookup: 'irrad', option: 'gcu'
    }
  ]
};

// plot configuration and custom functions
const yAxes = [
  {
    title: {
      text: 'Irradiance [W/m^2]'
    },
    showEmpty: false
  },
  {
    title: {
      text: 'Temperature [°C]'
    },
    showEmpty: false
  },
  {
    title: {
      text: 'Humidity [%]'
    },
    showEmpty: false
  },
  {
    title: {
      text: 'Pressure [mbar]'
    },
    showEmpty: false
  },
  {
    title: {
      text: 'Wind [m/s]'
    },
    showEmpty: false
  },
  {
    title: {
      text: 'Status'
    },
    showEmpty: false,
    opposite: true
  },
  {
    title: {
      text: 'Halt Cycle'
    },
    showEmpty: false,
    opposite: true
  },
  {
    title: {
      text: 'Direction [°]'
    },
    showEmpty: false,
    opposite: true
  },
  {
    title: {
      text: 'Voltage [V]'
    },
    showEmpty: false,
    opposite: true
  }
];
const lineOptions = {
  title: {
    text: 'Telemetry'
  },
  chart: {
    zoomType: 'x'
  },
  xAxis: {
    type: 'datetime',
    gridLineColor: '#E0E0E0',
    gridLineWidth: 1,
  },
  yAxis: yAxes,
  legend: {
    layout: 'horizontal',
    align: 'center',
    verticalAlign: 'bottom'
  },
  plotOptions: {
    series: {
      label: {
        connectorAllowed: false
      },
      marker: {
        enabled: false
      },
      pointStart: 5,
      animation: false,
      turboThreshold: 0
    }
  },
  series: [],
  responsive: {
    rules: [{
      condition: {
        maxWidth: 500
      },
      chartOptions: {
        legend: {
          layout: 'horizontal',
          align: 'center',
          verticalAlign: 'bottom'
        },
        yAxis: [
          { visible: false },
          { visible: false },
          { visible: false },
          { visible: false },
          { visible: false },
          { visible: false },
          { visible: false },
          { visible: false },
          { visible: false },
        ]
      }
    }]
  },
  credits: false,
  exporting: {
    sourceWidth: 1200,
    sourceHeight: 600
  }
};
const plot = {
  setTitle(title) {
    lineOptions.title.text = title;
  },
  clearSeries() {
    lineOptions.series = [];
  }
};

// options configuration function for api calls
const buildOpts = (type, telem, gcuId, lcu, startDate, endDate) => {
  const optsObj = {
    query: {
      startDate,
      endDate
    }
  };

  if (type === 'reed' || type === 'sensor' || type === 'status' && lcu) {
    optsObj.lcuId = lcu.id;
    optsObj.query.gcu = gcuId;
    optsObj.query.azi = telem.lookup === 'azimuth';
    optsObj.query.elev = telem.lookup === 'elevation';
  } else {
    optsObj.gcuId = gcuId;
  }

  if (type === 'reed' || type === 'sensor') {
    optsObj.query.dataFormat = 'degrees';
  }

  if (type === 'weather') {
    optsObj.query.temp = telem.lookup === 'temp';
    optsObj.query.irrad = telem.lookup === 'irrad';
    optsObj.query.wind = telem.lookup === 'wind';
  }

  if (type === 'irrad') {
    optsObj.query.type = telem.value;
  }

  if (type === 'reed') {
    optsObj.query.type = telem.subType;
  }

  if (type === 'status') {
    optsObj.query.statusFormat = telem.lookup === 'status' ? 'formatted' : 'raw';
  }

  return optsObj;
};

const getPlotAxis = (telem) => {
  let yAxis = null;

  if (telem.lookup === 'irrad') {
    yAxis = 0;
  } else if (telem.value === 'temperature') {
    yAxis = 1;
  } else if (telem.value === 'humidity') {
    yAxis = 2;
  } else if (telem.value === 'pressure') {
    yAxis = 3;
  } else if (telem.value === 'wind_speed' || telem.value === 'wind_gust') {
    yAxis = 4;
  } else if (telem.lookup === 'error_code' || telem.lookup === 'trackerStatus') {
    yAxis = 5;
  } else if (telem.lookup === 'halt_cycle') {
    yAxis = 6;
  } else if (telem.lookup === 'azimuth' || telem.lookup === 'elevation' || telem.value === 'wind_direction') {
    yAxis = 7;
  } else if (telem.lookup === 'total') {
    yAxis = 8;
  }

  return yAxis;
};

// functions for formatting data returned from the api call
const formatData = {
  weather({ telem, data, gcu }) {
    const outputData = {
      name: `${gcu.name} - ${telem.label}`,
      yAxis: getPlotAxis(telem),
      data: null
    };

    outputData.data = data.map(a => [moment(a.date).valueOf(), a[telem.value]]);

    return outputData;
  },
  irrad({ telem, data, gcu }) {
    const outputData = {
      name: `${gcu.name} - ${telem.label}`,
      yAxis: getPlotAxis(telem),
      data: null
    };

    outputData.data = data.map(a => [moment(a.date).valueOf(), a.value]);
    return outputData;
  },
  reed({
    telem,
    data,
    gcu,
    tracker,
    arm
  }) {
    const outputData = {
      name: null,
      yAxis: getPlotAxis(telem),
      data: null
    };

    if (telem.option === 'armature') {
      outputData.data = data.map(a => [moment(a.date).valueOf(), a[`arm${arm}`][telem.lookup]]);
      outputData.name = `${gcu.name} - ${tracker.name} - Arm ${arm} - ${telem.label}`;
    } else {
      outputData.dataBuffer = data.map(a => [moment(a.date).valueOf(), a[telem.lookup]]);
      outputData.name = `${gcu.name} - ${tracker.name} - ${telem.label}`;
    }

    return outputData;
  },
  sensor({
    telem,
    data,
    gcu,
    tracker,
    arm
  }) {
    const outputData = {
      name: `${gcu.name} - ${tracker.name} - Arm ${arm} - ${telem.label}`,
      yAxis: getPlotAxis(telem),
      data: null
    };

    outputData.data = data.map(a => [moment(a.date).valueOf(), a[`arm${arm}`][telem.lookup]]);

    return outputData;
  },
  status({
    telem,
    data,
    arm,
    gcu,
    tracker
  }) {
    const outputData = {
      name: null,
      yAxis: getPlotAxis(telem),
      data: null,
      tooltip: {
        pointFormat: null
      }
    };

    if (telem.option === 'armature' && telem.lookup === 'error_code') {
      outputData.data = data.map((a) => {
        return {
          x: moment(a.date).valueOf(),
          y: a[`arm${arm}`][telem.lookup],
          status: a[`arm${arm}`].error_name
        };
      });
      outputData.name = `${gcu.name} - ${tracker.name} - Arm ${arm} - ${telem.label}`;
      outputData.tooltip.pointFormat = '<span style="color:{point.color}">●</span> <b>{point.status}</b><br/>';
    } else if (telem.option === 'armature') {
      outputData.data = data.map((a) => {
        return {
          x: moment(a.date).valueOf(),
          y: a[`arm${arm}`][telem.lookup]
        };
      });
      outputData.name = `${gcu.name} - ${tracker.name} - Arm ${arm} - ${telem.label}`;
      outputData.tooltip.pointFormat = '<span style="color:{point.color}">●</span> {series.name}: <b>{point.y}</b><br/>';
    } else {
      outputData.data = data.map((a) => {
        return {
          x: moment(a.date).valueOf(),
          y: a.trackerStatusCode,
          status: a.trackerStatus
        };
      });
      outputData.name = `${gcu.name} - ${tracker.name} - ${telem.label}`;
      outputData.tooltip.pointFormat = '<span style="color:{point.color}">●</span> <b>{point.status}</b><br/>';
    }

    return outputData;
  }
};

export default {
  name: 'telemetry',
  components: {
    Multiselect,
    TrackerDropdown,
    GcuDropdown,
    SiteDropdown
  },
  data() {
    return {
      selectedGCU: null,
      selectedTracker: null,
      selectedArm: null,
      selectedTelemetry: null,
      telemOpts,
      colSpacing,
      lineOptions,
      armatures: [1, 2, 3, 4],
      disabled: {
        tracker: true,
        armature: true,
        search: true
      },
      show: {
        loading: false
      },
      date: {
				start: '',
				end: '',
				max: moment(new Date()).format()
      },
      inputsCollapsed: false
    };
  },
  methods: {
    getPlotData() {
      const startDate = moment(this.date.start, moment.ISO_8601).format('YYYY-MM-DD HH:mm:ss');
			const endDate = moment(this.date.end, moment.ISO_8601).format('YYYY-MM-DD HH:mm:ss');
      const telem = this.selectedTelemetry;
      const gcu = this.selectedGCU;
      const tracker = this.selectedTracker ? this.selectedTracker : null;
      const arm = this.selectedArm;

      // implemented in order to know when to stop the loading icon
      const telemLen = telem.length;
      let telemCount = 0;

      this.startLoading();
      try {
        telem.forEach(async (telemOpt) => {
          const seriesOpts = [];
          const reqOpts = buildOpts(telemOpt.type.toLowerCase(), telemOpt, gcu.id, tracker, startDate, endDate);
          const data = await this.$store.dispatch(`savanna/telemetry/get${telemOpt.type}Data`, reqOpts);
          telemCount++;
          this.stopLoading(telemCount, telemLen);
          if (data.length > 0) {
            if (arm && arm.length > 0) {
              arm.forEach((armNum) => {
                seriesOpts.push(formatData[telemOpt.type.toLowerCase()]({
                  data,
                  telem: telemOpt,
                  gcu,
                  tracker,
                  arm: armNum
                }));
              });
            } else {
              seriesOpts.push(formatData[telemOpt.type.toLowerCase()]({
                data,
                telem: telemOpt,
                gcu,
                tracker
              }));
            }
            if (seriesOpts.length > 0) {
              seriesOpts.forEach((series) => {
                this.lineOptions.series.push(series);
              });
            }
          } else {
            this.showNoData({ message: `${telemOpt.label} Data Unavailable` });
          }
        });
      } catch (err) {
        telemCount++;
        this.stopLoading(telemCount, telemLen);
        this.showNoData({ message: 'Error Fetching Data' });
      }
    },
    clearPlotData() {
      plot.setTitle('Telemetry');
      plot.clearSeries();
    },
    startLoading() {
      this.show.loading = true;
      this.disabled.search = true;
    },
    stopLoading(count, stopNum) {
      if (count === stopNum) {
        this.show.loading = false;
        this.disabled.search = false;
      }
    }
  },
  watch: {
    // these watchers are used to ensure that only the proper options have been selected
    // i.e. reset GCU list when the site is changed or removed
    selectedGCU() {
      this.selectedTelemetry = null;
      this.selectedTracker = null;

      if (this.selectedGCU === null) {
        this.disabled.tracker = true;
        this.telemOpts = [];
        this.selectedTelemetry = null;
      } else {
        this.disabled.tracker = false;
        this.telemOpts = [gcuTelemOpts];
      }
    },
    selectedTracker() {
      this.selectedArm = null;

      if (this.selectedTracker === null) {
        this.disabled.armature = true;
        if (this.selectedGCU !== null) {
          this.telemOpts = [gcuTelemOpts];
        }
      } else {
        this.disabled.armature = false;
        this.telemOpts = [gcuTelemOpts, trackerTelemOpts];
      }
    },
    selectedArm() {
      if (this.selectedArm === null) {
        this.telemOpts = [gcuTelemOpts, trackerTelemOpts];
      } else {
        this.telemOpts = [gcuTelemOpts, trackerTelemOpts, armatureTelemOpts];
      }
    },
    selectedTelemetry() {
      if (this.selectedTelemetry === null) {
        this.disabled.search = true;
      } else {
        this.disabled.search = false;
      }
    }
  },
  notifications: {
    showNoData: {
      type: 'error',
      title: 'Error'
    }
  }
};
</script>

<style scoped>
  #telemetryContainer{
    flex: 0 0 100%;
  }
  #plot .card-body  {
    display: flex;
    flex-direction: column;
  }
  .form-group {
    margin-bottom: 0.5rem;
  }
</style>
<style>
  /* hardcoded height for datetime inputs */
  /* TODO: determine a better way to handle this */
  #telemStartDate,
  #telemEndDate {
    height: 43px;
  }
</style>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
